import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Plus, Edit2, Trash2, ToggleLeft, ToggleRight, ExternalLink, 
  Image, Calendar, MapPin, Users, Target, BarChart3, Eye, 
  MousePointer, ChevronDown, ChevronUp, AlertCircle, Sparkles
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';

import { BACKEND_URL } from '../config/api';
import { todayYMD, addDaysYMD } from '../utils/dateHelpers';

const PLACEMENT_OPTIONS = [
  { value: 'no-results', label: 'No Results Page', maxDisplay: 1 },
  { value: 'employer-dashboard', label: 'Employer Dashboard', maxDisplay: 2 },
  { value: 'tradesperson-dashboard', label: 'Tradesperson Dashboard', maxDisplay: 2 },
  { value: 'resource-pages', label: 'Resource/Help Pages', maxDisplay: 2 }
];

const CATEGORY_OPTIONS = [
  { value: 'general', label: 'General' },
  { value: 'tools', label: 'Tools & Equipment' },
  { value: 'services', label: 'Business Services' },
  { value: 'insurance', label: 'Insurance' },
  { value: 'training', label: 'Training & Certification' },
  { value: 'software', label: 'Software & Apps' }
];

const AUDIENCE_OPTIONS = [
  { value: 'all', label: 'All Users' },
  { value: 'employer', label: 'Employers Only' },
  { value: 'tradesperson', label: 'Tradespeople Only' }
];

const STATUS_OPTIONS = [
  { value: 'draft', label: 'Draft', color: 'bg-slate-100 text-slate-600' },
  { value: 'active', label: 'Active', color: 'bg-green-100 text-green-700' },
  { value: 'paused', label: 'Paused', color: 'bg-yellow-100 text-yellow-700' },
  { value: 'expired', label: 'Expired', color: 'bg-red-100 text-red-600' }
];

const REGION_OPTIONS = [
  { value: 'all', label: 'All Regions' },
  { value: 'london', label: 'Greater London' },
  { value: 'surrey', label: 'Surrey' },
  { value: 'london,surrey', label: 'London & Surrey' }
];

export default function PartnerCampaignManager() {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [activeCount, setActiveCount] = useState(0);
  
  // Form state
  const [form, setForm] = useState({
    partner_name: '',
    logo_url: '',
    short_description: '',
    cta_text: 'Learn More',
    destination_url: '',
    category: 'general',
    target_audience: 'all',
    region: 'all',
    placement: 'employer-dashboard',
    priority: 5,
    start_date: todayYMD(),
    end_date: addDaysYMD(todayYMD(), 30),
    is_enabled: false,
    status: 'draft'
  });

  useEffect(() => {
    fetchCampaigns();
  }, []);

  const fetchCampaigns = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/partners/admin/partner-campaigns`, {
        withCredentials: true
      });
      setCampaigns(response.data.campaigns || []);
      setActiveCount(response.data.active_count || 0);
    } catch (error) {
      toast.error('Failed to load campaigns');
    } finally {
      setLoading(false);
    }
  };

  const fetchAnalytics = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/partners/admin/partner-campaigns/analytics`, {
        withCredentials: true
      });
      setAnalytics(response.data);
      setShowAnalytics(true);
    } catch (error) {
      toast.error('Failed to load analytics');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingCampaign) {
        await axios.put(
          `${BACKEND_URL}/api/partners/admin/partner-campaigns/${editingCampaign.campaign_id}`,
          form,
          { withCredentials: true }
        );
        toast.success('Campaign updated');
      } else {
        await axios.post(
          `${BACKEND_URL}/api/partners/admin/partner-campaigns`,
          form,
          { withCredentials: true }
        );
        toast.success('Campaign created');
      }
      
      setShowModal(false);
      resetForm();
      fetchCampaigns();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save campaign');
    }
  };

  const handleToggle = async (campaign) => {
    try {
      await axios.post(
        `${BACKEND_URL}/api/partners/admin/partner-campaigns/${campaign.campaign_id}/toggle`,
        {},
        { withCredentials: true }
      );
      fetchCampaigns();
      toast.success(campaign.is_enabled ? 'Campaign disabled' : 'Campaign enabled');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to toggle campaign');
    }
  };

  const handleDelete = async (campaign) => {
    if (!window.confirm(`Delete "${campaign.partner_name}" campaign?`)) return;
    
    try {
      await axios.delete(
        `${BACKEND_URL}/api/partners/admin/partner-campaigns/${campaign.campaign_id}`,
        { withCredentials: true }
      );
      toast.success('Campaign deleted');
      fetchCampaigns();
    } catch (error) {
      toast.error('Failed to delete campaign');
    }
  };

  const handleEdit = (campaign) => {
    setEditingCampaign(campaign);
    setForm({
      partner_name: campaign.partner_name,
      logo_url: campaign.logo_url || '',
      short_description: campaign.short_description,
      cta_text: campaign.cta_text,
      destination_url: campaign.destination_url,
      category: campaign.category,
      target_audience: campaign.target_audience,
      region: campaign.region,
      placement: campaign.placement,
      priority: campaign.priority,
      start_date: campaign.start_date?.split('T')[0] || '',
      end_date: campaign.end_date?.split('T')[0] || '',
      is_enabled: campaign.is_enabled,
      status: campaign.status
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setEditingCampaign(null);
    setForm({
      partner_name: '',
      logo_url: '',
      short_description: '',
      cta_text: 'Learn More',
      destination_url: '',
      category: 'general',
      target_audience: 'all',
      region: 'all',
      placement: 'employer-dashboard',
      priority: 5,
      start_date: todayYMD(),
      end_date: addDaysYMD(todayYMD(), 30),
      is_enabled: false,
      status: 'draft'
    });
  };

  const getStatusBadge = (status) => {
    const option = STATUS_OPTIONS.find(s => s.value === status);
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${option?.color || 'bg-slate-100'}`}>
        {option?.label || status}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 p-8">
        <div className="flex items-center justify-center">
          <div className="animate-spin h-8 w-8 border-4 border-orange-500 border-t-transparent rounded-full"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-slate-200" data-testid="partner-campaign-manager">
      {/* Header */}
      <div className="p-4 border-b border-slate-200">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-orange-500" />
              Partner Placements
            </h2>
            <p className="text-sm text-slate-500 mt-1">
              Manage curated partner campaigns across the platform
            </p>
          </div>
          <div className="flex items-center gap-3">
            <span className={`text-sm font-medium px-3 py-1 rounded-full ${
              activeCount >= 10 ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'
            }`}>
              {activeCount}/10 Active
            </span>
            <button
              onClick={fetchAnalytics}
              className="flex items-center gap-2 px-3 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
              data-testid="view-analytics-btn"
            >
              <BarChart3 className="h-4 w-4" />
              Analytics
            </button>
            <button
              onClick={() => {
                resetForm();
                setShowModal(true);
              }}
              disabled={campaigns.length >= 20}
              className="flex items-center gap-2 px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg font-medium transition-colors disabled:opacity-50"
              data-testid="add-campaign-btn"
            >
              <Plus className="h-4 w-4" />
              Add Campaign
            </button>
          </div>
        </div>
      </div>

      {/* Campaigns List */}
      <div className="divide-y divide-slate-100">
        {campaigns.length === 0 ? (
          <div className="p-8 text-center">
            <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Sparkles className="h-8 w-8 text-slate-400" />
            </div>
            <p className="text-slate-600 font-medium">No partner campaigns yet</p>
            <p className="text-sm text-slate-500 mt-1">Create your first campaign to get started</p>
          </div>
        ) : (
          campaigns.map((campaign) => (
            <div 
              key={campaign.campaign_id} 
              className={`p-4 hover:bg-slate-50 transition-colors ${!campaign.is_enabled ? 'opacity-60' : ''}`}
              data-testid={`campaign-row-${campaign.campaign_id}`}
            >
              <div className="flex items-start gap-4">
                {/* Logo */}
                <div className="flex-shrink-0 w-14 h-14 bg-slate-100 rounded-lg flex items-center justify-center overflow-hidden">
                  {campaign.logo_url ? (
                    <img src={campaign.logo_url} alt={campaign.partner_name} className="w-full h-full object-cover" />
                  ) : (
                    <Image className="h-6 w-6 text-slate-400" />
                  )}
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-semibold text-slate-900 truncate">{campaign.partner_name}</h3>
                    {getStatusBadge(campaign.status)}
                    {campaign.is_enabled && (
                      <span className="w-2 h-2 bg-green-500 rounded-full" title="Enabled"></span>
                    )}
                  </div>
                  <p className="text-sm text-slate-600 line-clamp-1 mb-2">{campaign.short_description}</p>
                  
                  {/* Meta info */}
                  <div className="flex flex-wrap items-center gap-3 text-xs text-slate-500">
                    <span className="flex items-center gap-1">
                      <Target className="h-3.5 w-3.5" />
                      {PLACEMENT_OPTIONS.find(p => p.value === campaign.placement)?.label}
                    </span>
                    <span className="flex items-center gap-1">
                      <Users className="h-3.5 w-3.5" />
                      {AUDIENCE_OPTIONS.find(a => a.value === campaign.target_audience)?.label}
                    </span>
                    <span className="flex items-center gap-1">
                      <MapPin className="h-3.5 w-3.5" />
                      {REGION_OPTIONS.find(r => r.value === campaign.region)?.label}
                    </span>
                    <span className="flex items-center gap-1">
                      <Calendar className="h-3.5 w-3.5" />
                      {new Date(campaign.start_date).toLocaleDateString()} - {new Date(campaign.end_date).toLocaleDateString()}
                    </span>
                    <span className="font-medium text-orange-600">Priority: {campaign.priority}</span>
                  </div>

                  {/* Stats */}
                  <div className="flex items-center gap-4 mt-2 text-xs">
                    <span className="flex items-center gap-1 text-slate-500">
                      <Eye className="h-3.5 w-3.5" />
                      {campaign.impression_count || 0} impressions
                    </span>
                    <span className="flex items-center gap-1 text-slate-500">
                      <MousePointer className="h-3.5 w-3.5" />
                      {campaign.click_count || 0} clicks
                    </span>
                    {campaign.impression_count > 0 && (
                      <span className="text-green-600 font-medium">
                        {((campaign.click_count || 0) / campaign.impression_count * 100).toFixed(1)}% CTR
                      </span>
                    )}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleToggle(campaign)}
                    className={`p-2 rounded-lg transition-colors ${
                      campaign.is_enabled 
                        ? 'text-green-600 hover:bg-green-50' 
                        : 'text-slate-400 hover:bg-slate-100'
                    }`}
                    title={campaign.is_enabled ? 'Disable' : 'Enable'}
                    data-testid={`toggle-btn-${campaign.campaign_id}`}
                  >
                    {campaign.is_enabled ? <ToggleRight className="h-5 w-5" /> : <ToggleLeft className="h-5 w-5" />}
                  </button>
                  <a
                    href={campaign.destination_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                    title="Open URL"
                  >
                    <ExternalLink className="h-5 w-5" />
                  </a>
                  <button
                    onClick={() => handleEdit(campaign)}
                    className="p-2 text-slate-400 hover:text-orange-600 hover:bg-orange-50 rounded-lg transition-colors"
                    title="Edit"
                    data-testid={`edit-btn-${campaign.campaign_id}`}
                  >
                    <Edit2 className="h-5 w-5" />
                  </button>
                  <button
                    onClick={() => handleDelete(campaign)}
                    className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    title="Delete"
                    data-testid={`delete-btn-${campaign.campaign_id}`}
                  >
                    <Trash2 className="h-5 w-5" />
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Create/Edit Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingCampaign ? 'Edit Campaign' : 'Create Partner Campaign'}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              {/* Partner Name */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Partner Name *
                </label>
                <input
                  type="text"
                  value={form.partner_name}
                  onChange={(e) => setForm({...form, partner_name: e.target.value})}
                  required
                  maxLength={100}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  placeholder="e.g., Toolstation"
                  data-testid="input-partner-name"
                />
              </div>

              {/* Logo URL */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Logo URL
                </label>
                <input
                  type="url"
                  value={form.logo_url}
                  onChange={(e) => setForm({...form, logo_url: e.target.value})}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  placeholder="https://..."
                  data-testid="input-logo-url"
                />
              </div>
            </div>

            {/* Short Description */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Short Description * <span className="text-slate-400">({form.short_description.length}/200)</span>
              </label>
              <textarea
                value={form.short_description}
                onChange={(e) => setForm({...form, short_description: e.target.value})}
                required
                maxLength={200}
                rows={2}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                placeholder="Brief description of the partner offer..."
                data-testid="input-description"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              {/* CTA Text */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Button Text
                </label>
                <input
                  type="text"
                  value={form.cta_text}
                  onChange={(e) => setForm({...form, cta_text: e.target.value})}
                  maxLength={50}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  placeholder="Learn More"
                  data-testid="input-cta-text"
                />
              </div>

              {/* Destination URL */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Destination URL *
                </label>
                <input
                  type="url"
                  value={form.destination_url}
                  onChange={(e) => setForm({...form, destination_url: e.target.value})}
                  required
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  placeholder="https://partner-website.com"
                  data-testid="input-destination-url"
                />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              {/* Category */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Category</label>
                <select
                  value={form.category}
                  onChange={(e) => setForm({...form, category: e.target.value})}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  data-testid="select-category"
                >
                  {CATEGORY_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>

              {/* Target Audience */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Target Audience</label>
                <select
                  value={form.target_audience}
                  onChange={(e) => setForm({...form, target_audience: e.target.value})}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  data-testid="select-audience"
                >
                  {AUDIENCE_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>

              {/* Region */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Region</label>
                <select
                  value={form.region}
                  onChange={(e) => setForm({...form, region: e.target.value})}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  data-testid="select-region"
                >
                  {REGION_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              {/* Placement */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Placement Location *</label>
                <select
                  value={form.placement}
                  onChange={(e) => setForm({...form, placement: e.target.value})}
                  required
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  data-testid="select-placement"
                >
                  {PLACEMENT_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label} (max {opt.maxDisplay})
                    </option>
                  ))}
                </select>
              </div>

              {/* Priority */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Priority (1-10) <span className="text-slate-400">Higher = shown first</span>
                </label>
                <input
                  type="number"
                  value={form.priority}
                  onChange={(e) => setForm({...form, priority: parseInt(e.target.value) || 5})}
                  min={1}
                  max={10}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  data-testid="input-priority"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              {/* Start Date */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Start Date *</label>
                <input
                  type="date"
                  value={form.start_date}
                  onChange={(e) => setForm({...form, start_date: e.target.value})}
                  required
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  data-testid="input-start-date"
                />
              </div>

              {/* End Date */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">End Date *</label>
                <input
                  type="date"
                  value={form.end_date}
                  onChange={(e) => setForm({...form, end_date: e.target.value})}
                  required
                  min={form.start_date}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  data-testid="input-end-date"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              {/* Status */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Status</label>
                <select
                  value={form.status}
                  onChange={(e) => setForm({...form, status: e.target.value})}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  data-testid="select-status"
                >
                  {STATUS_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>

              {/* Enabled Toggle */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Enabled</label>
                <button
                  type="button"
                  onClick={() => setForm({...form, is_enabled: !form.is_enabled})}
                  className={`w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg border transition-colors ${
                    form.is_enabled 
                      ? 'bg-green-50 border-green-200 text-green-700' 
                      : 'bg-slate-50 border-slate-200 text-slate-500'
                  }`}
                  data-testid="toggle-enabled"
                >
                  {form.is_enabled ? <ToggleRight className="h-5 w-5" /> : <ToggleLeft className="h-5 w-5" />}
                  {form.is_enabled ? 'Enabled' : 'Disabled'}
                </button>
              </div>
            </div>

            {/* Warning if enabling as active */}
            {form.is_enabled && form.status === 'active' && activeCount >= 10 && !editingCampaign && (
              <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-700 text-sm">
                <AlertCircle className="h-4 w-4" />
                Maximum 10 active campaigns reached. Disable another campaign first.
              </div>
            )}

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-4 border-t border-slate-200">
              <button
                type="button"
                onClick={() => setShowModal(false)}
                className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg font-medium transition-colors"
                data-testid="save-campaign-btn"
              >
                {editingCampaign ? 'Update Campaign' : 'Create Campaign'}
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Analytics Modal */}
      <Dialog open={showAnalytics} onOpenChange={setShowAnalytics}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-orange-500" />
              Partner Campaign Analytics
            </DialogTitle>
          </DialogHeader>

          {analytics && (
            <div className="space-y-6 mt-4">
              {/* Summary */}
              <div className="grid grid-cols-4 gap-4">
                <div className="bg-slate-50 rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold text-slate-900">{analytics.total_campaigns}</div>
                  <div className="text-xs text-slate-500">Campaigns</div>
                </div>
                <div className="bg-blue-50 rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold text-blue-600">{analytics.total_impressions.toLocaleString()}</div>
                  <div className="text-xs text-slate-500">Impressions</div>
                </div>
                <div className="bg-green-50 rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold text-green-600">{analytics.total_clicks.toLocaleString()}</div>
                  <div className="text-xs text-slate-500">Clicks</div>
                </div>
                <div className="bg-orange-50 rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold text-orange-600">{analytics.overall_ctr}%</div>
                  <div className="text-xs text-slate-500">Overall CTR</div>
                </div>
              </div>

              {/* By Placement */}
              <div>
                <h4 className="font-medium text-slate-900 mb-3">Performance by Placement</h4>
                <div className="space-y-2">
                  {Object.entries(analytics.by_placement || {}).map(([placement, data]) => (
                    <div key={placement} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                      <span className="font-medium text-slate-700">
                        {PLACEMENT_OPTIONS.find(p => p.value === placement)?.label || placement}
                      </span>
                      <div className="flex items-center gap-4 text-sm">
                        <span className="text-slate-500">{data.impressions} imp</span>
                        <span className="text-slate-500">{data.clicks} clicks</span>
                        <span className="font-medium text-green-600">
                          {data.impressions > 0 ? ((data.clicks / data.impressions) * 100).toFixed(1) : 0}% CTR
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Top Campaigns */}
              <div>
                <h4 className="font-medium text-slate-900 mb-3">Campaign Performance</h4>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {(analytics.campaigns || [])
                    .sort((a, b) => b.click_count - a.click_count)
                    .slice(0, 5)
                    .map((campaign) => (
                      <div key={campaign.campaign_id} className="flex items-center justify-between p-2 border-b border-slate-100">
                        <span className="text-sm text-slate-700 truncate max-w-[200px]">{campaign.partner_name}</span>
                        <div className="flex items-center gap-3 text-xs">
                          <span className="text-slate-500">{campaign.impression_count} imp</span>
                          <span className="text-slate-500">{campaign.click_count} clicks</span>
                          <span className={`font-medium ${campaign.ctr > 2 ? 'text-green-600' : 'text-slate-600'}`}>
                            {campaign.ctr}% CTR
                          </span>
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
